// Builds the LaunchDarkly identify context (ENG-1813).
//
// This exists as its own unit-testable module because the context is not just
// targeting input — with `bootstrap: "localStorage"` the SDK derives its cache
// key from `btoa(JSON.stringify(context))`, so **every byte of this object is
// part of a localStorage key**. An attribute that varies between two otherwise
// identical page loads silently orphans a ~100KB cache entry each time, and
// the SDK never collects them. See utils/launchdarkly-storage.util.ts.
//
// Rule for anything added here: the value must be stable for a given user
// across page loads. If it can't be, leave it out.

import type { CurrentUser } from "features/auth/auth-slice";

export interface LdUserContext {
  kind: "user";
  key: string;
  email: string;
  name: string;
  avatar?: string;
  firstName: string;
  lastName: string;
  custom: {
    practice?: string;
    practiceId?: number;
    roles: string[];
  };
}

/**
 * Strips the query string from a media URL.
 *
 * `User.photo` is an ImageField on the default S3 storage, which leaves
 * django-storages' `querystring_auth` at its `True` default — only
 * `PublicStorage` (clinic/practice logos) turns it off. So the serialized
 * value is a pre-signed URL whose `X-Amz-Date` / `X-Amz-Signature` are
 * regenerated on every `/auth/users/me/`, making the raw value different on
 * every page load. The path identifies the object just as well and is stable,
 * which keeps the attribute useful in the LaunchDarkly dashboard without
 * turning it into a cache-buster.
 */
export function toStableMediaUrl(url: string | undefined | null): string {
  if (!url) return "";
  const queryStart = url.indexOf("?");
  return queryStart === -1 ? url : url.slice(0, queryStart);
}

/**
 * Builds a LaunchDarkly context that is byte-identical across page loads for
 * an unchanged user, so the SDK reuses one bootstrap cache entry instead of
 * orphaning a new one each time.
 */
export function buildStableLdContext(
  userKey: string,
  user: CurrentUser
): LdUserContext {
  const context: LdUserContext = {
    kind: "user",
    key: userKey,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    firstName: user.first_name,
    lastName: user.last_name,
    custom: {
      practice: user.practice?.name,
      practiceId: user.practice?.id,
      // Sorted because the backend does not order this M2M, so the same group
      // set can serialize in a different order between requests — which would
      // change the JSON, and therefore the cache key, on an unchanged user.
      roles: [...user.groups.map((group) => group.name)].sort()
    }
  };

  // Omit the key entirely rather than emitting `avatar: ""`, so a user with no
  // photo and a user whose photo failed to serialize produce the same context.
  const avatar = toStableMediaUrl(user.photo);
  if (avatar) context.avatar = avatar;

  return context;
}
